import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles,
    Wind,
    Users,
    BookOpen,
    Layers,
    Music,
    ArrowRight,
    Quote,
    Instagram,
    MessageCircle,
} from "lucide-react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "../components/ui/button";
import { IMAGES, SERVICES, BOOKABLE_SERVICES, WHO_THIS_IS_FOR, BLOG_POSTS, SITE } from "../data/content";
import SectionHeading from "../components/SectionHeading";
import LeadMagnet from "../components/LeadMagnet";
import TrustStrip from "../components/TrustStrip";
import FAQSection from "../components/FAQSection";
import MaskedLines from "../components/MaskedLines";
import EditorialMarquee from "../components/EditorialMarquee";
import ManifestoSection from "../components/ManifestoSection";
import useReveal from "../hooks/useReveal";

const ICONS = { Sparkles, Wind, Users, BookOpen, Layers, Music };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
    useReveal();
    const [testimonials, setTestimonials] = useState([]);
    const [active, setActive] = useState(0);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const heroImageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const heroImageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
    const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

    useEffect(() => {
        axios
            .get(`${API}/testimonials`)
            .then((r) => setTestimonials(r.data || []))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO — kinetic on-load reveal + parallax */}
            <section
                ref={heroRef}
                className="relative min-h-[78vh] lg:min-h-[85vh] flex items-end pt-24 pb-16 lg:pb-24 overflow-hidden"
                data-testid="hero-section"
            >
                <motion.div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${IMAGES.hero})`,
                        y: heroImageY,
                        scale: heroImageScale,
                    }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 ssh-hero-overlay" aria-hidden="true" />

                {/* Grain overlay */}
                <div
                    className="absolute inset-0 opacity-[0.045] mix-blend-multiply pointer-events-none"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    }}
                    aria-hidden="true"
                />

                <motion.div
                    style={{ y: heroTextY }}
                    className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full"
                >
                    <motion.p
                        className="ssh-eyebrow flex items-center gap-3"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <span className="inline-block w-8 h-px bg-[#A68A6B]" />
                        Soul Sync Healing · Est. 2025
                    </motion.p>

                    <MaskedLines
                        as="h1"
                        lines={[
                            "A deeper approach to healing —",
                            "beyond just the mind.",
                        ]}
                        baseDelay={0.15}
                        stagger={0.12}
                        className="mt-6 font-serif font-light tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-[#2C2B29] max-w-4xl"
                        lineClassName="[&_em]:italic [&_em]:text-[#A68A6B]"
                    />

                    <motion.p
                        className="mt-10 max-w-2xl text-base sm:text-lg lg:text-xl leading-[1.7] text-[#4A4947]"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                        Integrating therapeutic practices, body-based
                        approaches and expressive mediums to support emotional
                        healing, self-awareness and meaningful life change.
                    </motion.p>

                    <motion.div
                        className="mt-10 flex flex-wrap items-center gap-4"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Link to="/contact#booking">
                            <Button
                                className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                data-testid="hero-book-session"
                            >
                                Book a Session
                            </Button>
                        </Link>
                        <Link to="/services">
                            <Button
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] transition-colors"
                                data-testid="hero-explore-services"
                            >
                                Explore Services
                            </Button>
                        </Link>
                    </motion.div>

                    {/* Scroll cue */}
                    <motion.div
                        className="absolute bottom-6 right-6 lg:right-12 flex flex-col items-end gap-4 text-[10px] uppercase tracking-[0.32em] text-[#4A4947]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 1.15 }}
                        aria-hidden="true"
                    >
                        <span>Scroll gently</span>
                        <motion.span
                            className="block w-px h-16 bg-[#A68A6B] origin-top"
                            animate={{ scaleY: [0.2, 1, 0.2] }}
                            transition={{
                                duration: 2.6,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        />
                    </motion.div>
                </motion.div>
            </section>

            {/* TRUST STRIP */}
            <TrustStrip />

            {/* EDITORIAL MARQUEE */}
            <EditorialMarquee />

            {/* INTRO STRIP */}
            <section className="bg-[#FAF8F5]" data-testid="intro-strip">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                    <div className="lg:col-span-4">
                        <span className="ssh-divider" />
                        <p className="ssh-eyebrow mt-4">A safe space to begin</p>
                    </div>
                    <div className="lg:col-span-8">
                        <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#2C2B29]">
                            Healing isn't loud. It's the quiet, daily return to a self
                            that finally feels safe to soften —{" "}
                            <em className="italic text-[#A68A6B]">
                                in body, in mind, in soul.
                            </em>
                        </p>
                    </div>
                </div>
            </section>

            {/* ABOUT SHREYA TEASER */}
            <section
                className="bg-[#EFEBE4]"
                data-testid="about-teaser"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-5 ssh-reveal">
                        <div className="relative aspect-[4/5] overflow-hidden bg-[#2C2B29]">
                            <img
                                src={IMAGES.shreya}
                                alt="Portrait of Shreya, founder of Soul Sync Healing"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7 ssh-reveal">
                        <p className="ssh-eyebrow">Meet Shreya</p>
                        <h2 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-4xl lg:text-5xl text-[#2C2B29]">
                            A psycho-spiritual coach{" "}
                            <em className="italic text-[#A68A6B]">
                                holding space for your return.
                            </em>
                        </h2>
                        <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#4A4947]">
                            Shreya is a Clinical Hypnotherapist and Psycho-Spiritual
                            Coach who blends modern psychology, subconscious healing,
                            emotional wellness, and intuitive guidance to help you
                            heal deeply and reconnect with yourself.
                        </p>
                        <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#4A4947]">
                            Her work is grounded, gentle, and devotional — built on
                            the belief that you don't need to be fixed. You need to be
                            met.
                        </p>
                        <div className="mt-8">
                            <Link
                                to="/about"
                                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                                data-testid="home-about-link"
                            >
                                Read Shreya's full story
                                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* MANIFESTO — numbered chapters on dark backdrop */}
            <ManifestoSection />

            {/* SERVICES GRID */}
            <section className="bg-white" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                        <div className="lg:col-span-7">
                            <SectionHeading
                                eyebrow="Services"
                                title="Modalities woven into"
                                titleItalic="one tender practice."
                                sub="Six core offerings — used alone or layered together — to meet you wherever you are in your healing."
                            />
                        </div>
                        <div className="lg:col-span-5 lg:flex lg:items-end lg:justify-end">
                            <Link
                                to="/services"
                                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                                data-testid="home-services-all"
                            >
                                All services
                                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EFEBE4] border border-[#EFEBE4]">
                        {SERVICES.map((s, i) => {
                            const Icon = ICONS[s.icon];
                            return (
                                <Link
                                    key={s.slug}
                                    to={`/services#${s.slug}`}
                                    className="group bg-white p-10 lg:p-12 transition-colors hover:bg-[#FAF8F5] ssh-reveal"
                                    style={{ transitionDelay: `${0.05 * i}s` }}
                                    data-testid={`home-service-${s.slug}`}
                                >
                                    <Icon
                                        className="h-7 w-7 text-[#A68A6B]"
                                        strokeWidth={1.25}
                                    />
                                    <h3 className="mt-8 font-serif text-2xl text-[#2C2B29] leading-tight">
                                        {s.name}
                                    </h3>
                                    <p className="mt-4 text-sm text-[#4A4947] leading-relaxed">
                                        {s.short}
                                    </p>
                                    <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] group-hover:gap-3 transition-all">
                                        Read more <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PACKAGES */}
            <section className="bg-[#FAF8F5]" data-testid="packages-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="ssh-reveal">
                        <SectionHeading
                            eyebrow="Sessions & Programs"
                            title="Three gentle ways"
                            titleItalic="to begin."
                            sub="A first conversation, a single deep session, or a fully personalised eight-session container — each confirmed instantly with secure online payment."
                        />
                    </div>
                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {BOOKABLE_SERVICES.map((p, i) => (
                            <article
                                key={p.slug}
                                className="ssh-card p-10 flex flex-col ssh-reveal"
                                style={{ transitionDelay: `${0.08 * i}s` }}
                                data-testid={`home-package-${p.slug}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <p className="ssh-eyebrow">{p.eyebrow}</p>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] border border-[#8A9A86] px-2 py-1 whitespace-nowrap">
                                        {p.priceLabel}
                                    </span>
                                </div>
                                <h3 className="mt-5 font-serif text-2xl lg:text-3xl text-[#2C2B29] leading-tight">
                                    {p.name}
                                </h3>
                                <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[#8A9A86]">
                                    {p.duration}
                                </p>
                                {p.savingsLabel && (
                                    <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#A68A6B]">
                                        {p.valueLabel} · {p.savingsLabel}
                                    </p>
                                )}
                                <p className="mt-4 text-sm text-[#4A4947] leading-relaxed">
                                    {p.short}
                                </p>
                                <div className="mt-auto pt-8 flex items-center justify-between gap-4">
                                    <Link
                                        to={`/packages/${p.slug}`}
                                        className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] inline-flex items-center gap-2"
                                        data-testid={`home-package-explore-${p.slug}`}
                                    >
                                        Learn more <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </Link>
                                    <Link
                                        to={`/contact?service=${p.slug}#booking`}
                                        className="text-xs uppercase tracking-[0.22em] text-[#2C2B29] hover:text-[#A68A6B] inline-flex items-center gap-2 transition-colors"
                                        data-testid={`home-package-book-${p.slug}`}
                                    >
                                        Book
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO THIS IS FOR */}
            <section
                className="relative bg-[#2C2B29]"
                data-testid="who-this-is-for"
            >
                <div className="absolute inset-0 opacity-30">
                    <img
                        src={IMAGES.calmNature}
                        alt=""
                        className="w-full h-full object-cover"
                        aria-hidden="true"
                    />
                </div>
                <div className="absolute inset-0 bg-[#2C2B29]/55" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                        <div className="lg:col-span-5">
                            <SectionHeading
                                eyebrow="Who this is for"
                                title="You don't have to keep"
                                titleItalic="holding it all alone."
                                light
                            />
                        </div>
                        <div className="lg:col-span-7">
                            <ul className="space-y-5">
                                {WHO_THIS_IS_FOR.map((line, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-4 text-lg leading-relaxed text-[#EFEBE4] ssh-reveal"
                                        style={{ transitionDelay: `${0.05 * i}s` }}
                                    >
                                        <span className="text-[#C5A880] mt-3 inline-block w-6 h-px bg-[#C5A880]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* LEAD MAGNET */}
            <LeadMagnet />

            {/* TESTIMONIALS */}
            <section className="bg-[#EFEBE4]" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="ssh-reveal">
                        <SectionHeading
                            eyebrow="Reflections"
                            title="From those who walked"
                            titleItalic="this path before you."
                        />
                    </div>
                    {testimonials.length > 0 && (
                        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                            <div className="lg:col-span-8 ssh-reveal">
                                <Quote
                                    className="h-10 w-10 text-[#A68A6B]"
                                    strokeWidth={1}
                                />
                                <blockquote className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#2C2B29]">
                                    “{testimonials[active]?.quote}”
                                </blockquote>
                                <div className="mt-8 flex items-center gap-4">
                                    <span className="block w-10 h-px bg-[#A68A6B]" />
                                    <div>
                                        <p className="text-sm uppercase tracking-[0.18em] text-[#2C2B29]">
                                            {testimonials[active]?.name}
                                        </p>
                                        <p className="text-xs text-[#75736F] mt-1">
                                            {testimonials[active]?.location} ·{" "}
                                            {testimonials[active]?.service}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="lg:col-span-4 ssh-reveal">
                                <p className="ssh-eyebrow">Stories</p>
                                <ul className="mt-6 space-y-3">
                                    {testimonials.map((t, i) => (
                                        <li key={t.id}>
                                            <button
                                                onClick={() => setActive(i)}
                                                className={`text-left w-full py-3 border-b border-[#dfd9cc] text-sm transition-colors ${
                                                    active === i
                                                        ? "text-[#A68A6B]"
                                                        : "text-[#4A4947] hover:text-[#A68A6B]"
                                                }`}
                                                data-testid={`testimonial-tab-${i}`}
                                            >
                                                {t.name} —{" "}
                                                <span className="text-xs italic">
                                                    {t.service}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* BLOG TEASER */}
            <section className="bg-white" data-testid="blog-teaser">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 ssh-reveal">
                        <div className="lg:col-span-7">
                            <SectionHeading
                                eyebrow="Insights"
                                title="Slow reads on healing,"
                                titleItalic="softness, and self-return."
                            />
                        </div>
                        <div className="lg:col-span-5 lg:flex lg:items-end lg:justify-end">
                            <Link
                                to="/blog"
                                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                            >
                                All insights <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
                        {BLOG_POSTS.slice(0, 3).map((p, i) => (
                            <Link
                                key={p.slug}
                                to={`/blog/${p.slug}`}
                                className="group ssh-reveal"
                                style={{ transitionDelay: `${0.07 * i}s` }}
                                data-testid={`home-blog-${p.slug}`}
                            >
                                <p className="ssh-eyebrow">{p.category}</p>
                                <h3 className="mt-3 font-serif text-2xl text-[#2C2B29] leading-tight group-hover:text-[#A68A6B] transition-colors">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#4A4947] leading-relaxed">
                                    {p.excerpt}
                                </p>
                                <p className="mt-5 text-xs uppercase tracking-[0.18em] text-[#75736F]">
                                    {p.readTime}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FAQSection />

            {/* CONTACT CTA */}
            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]" data-testid="home-cta">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center ssh-reveal">
                    <p className="ssh-eyebrow">Begin Your Healing Journey</p>
                    <h2 className="mt-6 font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                        When you're ready,{" "}
                        <em className="italic text-[#A68A6B]">we begin slowly.</em>
                    </h2>
                    <p className="mt-6 max-w-2xl mx-auto text-lg leading-relaxed text-[#4A4947]">
                        Whether you're called to a single session or a deeper container,
                        you're welcome here. Reach out — there's no pressure, only space.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link to="/contact#booking">
                            <Button
                                className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                data-testid="cta-book-session"
                            >
                                Book a Session
                            </Button>
                        </Link>
                        <a
                            href={`https://wa.me/${SITE.whatsappRaw}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="cta-whatsapp"
                        >
                            <Button
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 transition-colors"
                            >
                                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                                WhatsApp
                            </Button>
                        </a>
                        <a
                            href={SITE.instagramUrl}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="cta-instagram"
                        >
                            <Button
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 transition-colors"
                            >
                                <Instagram className="h-4 w-4" strokeWidth={1.5} />
                                Instagram
                            </Button>
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
